import axiosInstance from '../../../config/axios';

export type PartnerKind = 'CUSTOMER' | 'PARTNER' | 'SUPPLIER';

export interface Partner {
  id: number;
  kind: PartnerKind;
  name: string;
}

export interface ProjectCounts {
  LEAD: number;
  OFFER_CREATED: number;
  OFFER_SENT: number;
  ORDER_CONFIRMED: number;
  IN_EXECUTION: number;
  COMPLETED: number;
  WARRANTY: number;
  total: number;
}

export interface OfferCounts {
  DRAFT: number;
  SENT: number;
  ACCEPTED: number;
  REJECTED: number;
  EXPIRED: number;
  total: number;
}

export interface OfferAmounts {
  ACCEPTED: number;
  OPEN_PIPELINE: number;
}

export interface LastOffer {
  id: number;
  offer_number: string;
  title: string;
  status: string;
  gross_total: number;
  created_at: string;
}

export interface OffersSummary {
  counts: OfferCounts;
  amounts: OfferAmounts;
  last_offer: LastOffer | null;
}

export interface InvoiceCounts {
  DRAFT: number;
  SENT: number;
  PAID: number;
  CANCELLED: number;
  total: number;
  overdue: number;
}

export interface InvoiceAmounts {
  total_invoiced: number;
  paid: number;
  open: number;
  overdue: number;
}

export interface InvoicesSummary {
  counts: InvoiceCounts;
  amounts: InvoiceAmounts;
}

export type PaymentBehaviorLabel = 'good' | 'ok' | 'late';
export type PaymentBehaviorIcon = 'smiley' | 'neutral' | 'sad';

export interface PaymentBehaviorDetails {
  overdue_ratio: number;
  overdue_invoices: number;
  total_invoices: number;
}

export interface PaymentBehavior {
  score: number;
  label: PaymentBehaviorLabel;
  icon: PaymentBehaviorIcon;
  details: PaymentBehaviorDetails;
}

export interface TasksSummary {
  OPEN: number;
  IN_PROGRESS: number;
  DONE: number;
  overdue: number;
  next_due: string | null;
}

export type IntervalUnit = 'MONTH' | 'QUARTER' | 'YEAR';

export interface ServiceContractItem {
  id: number;
  name: string;
  amount_gross: number;
  interval_unit: IntervalUnit;
  status: string;
  start_date: string;
}

export interface ServiceContractsSummary {
  active_count: number;
  mrr_gross: number;
  items: ServiceContractItem[];
}

export interface PaymentPlansSummary {
  plans: number;
  milestones_total: number;
  milestones_invoiced: number;
  milestones_remaining: number;
  planned_gross_total: number;
  planned_gross_remaining: number;
}

export interface PartnerSalesSummary {
  partner: Partner;
  projects: ProjectCounts;
  offers: OffersSummary;
  invoices: InvoicesSummary;
  payment_behavior: PaymentBehavior;
  tasks: TasksSummary;
  service_contracts: ServiceContractsSummary;
  payment_plans: PaymentPlansSummary;
}

function parseAmount(value: string | number | undefined | null): number {
  if (value === undefined || value === null) return 0;
  if (typeof value === 'number') return value;
  return parseFloat(value) || 0;
}

function parseLabel(value: string | undefined | null): PaymentBehaviorLabel {
  if (value === 'good' || value === 'ok' || value === 'late') return value;
  return 'ok';
}

function parseIcon(value: string | undefined | null): PaymentBehaviorIcon {
  if (value === 'smiley' || value === 'neutral' || value === 'sad') return value;
  return 'neutral';
}

export default class BusinessPartnerSalesService {
  static async getSalesSummary(partnerId: number): Promise<PartnerSalesSummary> {
    const response = await axiosInstance.get(`/api/admin/business-partners/${partnerId}/sales-summary/`);
    const raw = response.data;

    return {
      partner: {
        id: raw.partner?.id ?? partnerId,
        kind: raw.partner?.kind ?? 'CUSTOMER',
        name: raw.partner?.name ?? '',
      },
      projects: {
        LEAD: raw.projects?.LEAD ?? 0,
        OFFER_CREATED: raw.projects?.OFFER_CREATED ?? 0,
        OFFER_SENT: raw.projects?.OFFER_SENT ?? 0,
        ORDER_CONFIRMED: raw.projects?.ORDER_CONFIRMED ?? 0,
        IN_EXECUTION: raw.projects?.IN_EXECUTION ?? 0,
        COMPLETED: raw.projects?.COMPLETED ?? 0,
        WARRANTY: raw.projects?.WARRANTY ?? 0,
        total: raw.projects?.total ?? 0,
      },
      offers: {
        counts: {
          DRAFT: raw.offers?.counts?.DRAFT ?? 0,
          SENT: raw.offers?.counts?.SENT ?? 0,
          ACCEPTED: raw.offers?.counts?.ACCEPTED ?? 0,
          REJECTED: raw.offers?.counts?.REJECTED ?? 0,
          EXPIRED: raw.offers?.counts?.EXPIRED ?? 0,
          total: raw.offers?.counts?.total ?? 0,
        },
        amounts: {
          ACCEPTED: parseAmount(raw.offers?.amounts?.ACCEPTED),
          OPEN_PIPELINE: parseAmount(raw.offers?.amounts?.OPEN_PIPELINE),
        },
        last_offer: raw.offers?.last_offer ? {
          id: raw.offers.last_offer.id,
          offer_number: raw.offers.last_offer.offer_number,
          title: raw.offers.last_offer.title,
          status: raw.offers.last_offer.status,
          gross_total: parseAmount(raw.offers.last_offer.gross_total),
          created_at: raw.offers.last_offer.created_at,
        } : null,
      },
      invoices: {
        counts: {
          DRAFT: raw.invoices?.counts?.DRAFT ?? 0,
          SENT: raw.invoices?.counts?.SENT ?? 0,
          PAID: raw.invoices?.counts?.PAID ?? 0,
          CANCELLED: raw.invoices?.counts?.CANCELLED ?? 0,
          total: raw.invoices?.counts?.total ?? 0,
          overdue: raw.invoices?.counts?.overdue ?? 0,
        },
        amounts: {
          total_invoiced: parseAmount(raw.invoices?.amounts?.total_invoiced),
          paid: parseAmount(raw.invoices?.amounts?.paid),
          open: parseAmount(raw.invoices?.amounts?.open),
          overdue: parseAmount(raw.invoices?.amounts?.overdue),
        },
      },
      payment_behavior: {
        score: raw.payment_behavior?.score ?? 0,
        label: parseLabel(raw.payment_behavior?.label),
        icon: parseIcon(raw.payment_behavior?.icon),
        details: {
          overdue_ratio: raw.payment_behavior?.details?.overdue_ratio ?? 0,
          overdue_invoices: raw.payment_behavior?.details?.overdue_invoices ?? 0,
          total_invoices: raw.payment_behavior?.details?.total_invoices ?? 0,
        },
      },
      tasks: {
        OPEN: raw.tasks?.OPEN ?? 0,
        IN_PROGRESS: raw.tasks?.IN_PROGRESS ?? 0,
        DONE: raw.tasks?.DONE ?? 0,
        overdue: raw.tasks?.overdue ?? 0,
        next_due: raw.tasks?.next_due ?? null,
      },
      service_contracts: {
        active_count: raw.service_contracts?.active_count ?? 0,
        mrr_gross: parseAmount(raw.service_contracts?.mrr_gross),
        items: (raw.service_contracts?.items ?? []).map((item: {
          id?: number;
          name?: string;
          amount_gross?: string | number;
          interval_unit?: string;
          status?: string;
          start_date?: string;
        }) => ({
          id: item.id ?? 0,
          name: item.name ?? '',
          amount_gross: parseAmount(item.amount_gross),
          interval_unit: (item.interval_unit as IntervalUnit) ?? 'MONTH',
          status: item.status ?? 'ACTIVE',
          start_date: item.start_date ?? '',
        })),
      },
      payment_plans: {
        plans: raw.payment_plans?.plans ?? 0,
        milestones_total: raw.payment_plans?.milestones_total ?? 0,
        milestones_invoiced: raw.payment_plans?.milestones_invoiced ?? 0,
        milestones_remaining: (raw.payment_plans?.milestones_total ?? 0) - (raw.payment_plans?.milestones_invoiced ?? 0),
        planned_gross_total: parseAmount(raw.payment_plans?.planned_gross_total),
        planned_gross_remaining: parseAmount(raw.payment_plans?.planned_gross_remaining),
      },
    };
  }

  static formatCurrency(amount: number): string {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  }

  static getPaymentBehaviorEmoji(icon: PaymentBehaviorIcon): string {
    switch (icon) {
      case 'smiley':
        return '🙂';
      case 'neutral':
        return '😐';
      case 'sad':
        return '😟';
      default:
        return '😐';
    }
  }

  static getPaymentBehaviorColor(icon: PaymentBehaviorIcon): { bg: string; text: string } {
    switch (icon) {
      case 'smiley':
        return { bg: '#d1fae5', text: '#065f46' };
      case 'neutral':
        return { bg: '#fef3c7', text: '#92400e' };
      case 'sad':
        return { bg: '#fee2e2', text: '#991b1b' };
      default:
        return { bg: '#f3f4f6', text: '#374151' };
    }
  }
}
